import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getUserKey, getCachedUserKey, clearUserKeyCache } from './userKey';
import { useCallback, useRef } from 'react';

const LICENSE_CACHE_KEY = 'license_cache';
const LICENSE_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export interface LicenseValidationResponse {
  isValid: boolean;
  expiresAt?: Date;
}

interface LicenseCache {
  valid: boolean;
  expiresAt: string;
  timestamp: number;
}

const getCachedLicense = (userEmail: string): LicenseCache | null => {
  try {
    const cache = localStorage.getItem(`${LICENSE_CACHE_KEY}_${userEmail}`);
    if (!cache) return null;
    
    const parsedCache = JSON.parse(cache) as LicenseCache;
    const now = Date.now();
    
    // Check if cache is still valid (within 24 hours)
    if (now - parsedCache.timestamp > LICENSE_CACHE_DURATION) {
      localStorage.removeItem(`${LICENSE_CACHE_KEY}_${userEmail}`);
      return null;
    }
    
    return parsedCache;
  } catch (error) {
    console.error('Error reading license cache:', error);
    return null;
  }
};

const setCachedLicense = (userEmail: string, data: LicenseValidationResponse) => {
  try {
    if (data.isValid && data.expiresAt) {
      const cache: LicenseCache = {
        valid: data.isValid,
        expiresAt: data.expiresAt.toISOString(),
        timestamp: Date.now(),
      };
      localStorage.setItem(`${LICENSE_CACHE_KEY}_${userEmail}`, JSON.stringify(cache));
    }
  } catch (error) {
    console.error('Error caching license:', error);
  }
};

export const checkUserLicense = async (userId: string, userEmail: string): Promise<LicenseValidationResponse> => {
  // First try to get cached license
  const cachedLicense = getCachedLicense(userEmail);
  if (cachedLicense) {
    console.log('Using cached license data');
    return {
      isValid: cachedLicense.valid,
      expiresAt: cachedLicense.expiresAt ? new Date(cachedLicense.expiresAt) : undefined,
    };
  }

  console.log('Checking license from Firestore for user:', userId);

  try {
    // Read license from Firestore
    const licenseRef = doc(db, 'users', userId, 'license', 'current');
    const licenseDoc = await getDoc(licenseRef);

    if (!licenseDoc.exists()) {
      console.log('No license document found in Firestore');
      return { isValid: false };
    }

    const licenseData = licenseDoc.data();
    console.log('License data from Firestore:', licenseData);

    // Check if subscription is active
    const isActive = ['active', 'on_trial', 'past_due'].includes(licenseData.status);

    const result = {
      isValid: isActive,
      expiresAt: licenseData.renewsAt ? new Date(licenseData.renewsAt) : undefined,
    };

    // Cache the license check
    if (isActive) {
      setCachedLicense(userEmail, result);
    }

    return result;
  } catch (error) {
    console.error('Error checking user license from Firestore:', error);
    // If we have cached data, use it even if it's expired
    const expiredCache = getCachedLicense(userEmail);
    if (expiredCache) {
      console.log('Using expired cached license data due to error');
      return {
        isValid: expiredCache.valid,
        expiresAt: expiredCache.expiresAt ? new Date(expiredCache.expiresAt) : undefined,
      };
    }
    return { isValid: false };
  }
};

/**
 * Manually refresh license from LemonSqueezy API via Cloud Function
 */
export const refreshLicenseFromAPI = async (userEmail: string): Promise<LicenseValidationResponse> => {
  const functions = getFunctions();
  const refreshLicense = httpsCallable(functions, 'refreshLicense');

  try {
    const result = await refreshLicense({ email: userEmail });
    const data = result.data as any;

    if (data.hasLicense) {
      return {
        isValid: ['active', 'on_trial', 'past_due'].includes(data.status),
        expiresAt: data.expiresAt ? new Date(data.expiresAt) : undefined,
      };
    }

    return { isValid: false };
  } catch (error) {
    console.error('Error refreshing license from API:', error);
    throw error;
  }
};

export const useLicenseCheck = () => {
  const [hasValidLicense, setHasValidLicense] = useState(false);
  const [loading, setLoading] = useState(true);
  const [licenseKey, setLicenseKey] = useState<string | null>(null);
  // licenseKeyLoading: the encryption key is still being resolved (waiting on license
  // status or an in-flight fetch). licenseKeyError: the fetch failed (transient —
  // network/rate limit/server), which is NOT the same as "user has no key".
  const [licenseKeyLoading, setLicenseKeyLoading] = useState(true);
  const [licenseKeyError, setLicenseKeyError] = useState<string | null>(null);
  const [customerPortalUrl, setCustomerPortalUrl] = useState<string | null>(null);
  const { user } = useAuth();
  const userUidRef = useRef<string | null>(null);
  userUidRef.current = user?.uid ?? null;

  // Reset license state to "loading" SYNCHRONOUSLY when the signed-in user
  // changes — including auth restoring the user after a page refresh. The
  // license effect below re-runs only after the next commit, but ProtectedRoute
  // renders <Navigate to="/settings"> during render; without this, the first
  // render after auth restores the user still sees the stale no-user result
  // (loading=false, hasValidLicense=false), reads it as "no license", and
  // bounces the user off their page before the effect can react. Adjusting
  // state during render is React's documented pattern for this case.
  const [licenseUserUid, setLicenseUserUid] = useState<string | null>(user?.uid ?? null);
  if ((user?.uid ?? null) !== licenseUserUid) {
    setLicenseUserUid(user?.uid ?? null);
    if (user?.uid) {
      setLoading(true);
      setHasValidLicense(false);
    }
  }

  // Separate effect for license validation (from Firestore)
  useEffect(() => {
    if (!user?.uid || !user?.email) {
      setHasValidLicense(false);
      setLoading(false);
      setCustomerPortalUrl(null);
      return;
    }

    // A user is present but the license snapshot hasn't arrived yet. Mark loading
    // so downstream consumers see hasActiveLicense === null (undetermined), not
    // false. Without this, a page refresh runs this effect first with no user
    // (setting loading=false) and then again once auth restores the user — and
    // the stale loading=false + not-yet-loaded hasValidLicense=false reads as
    // "no license", bouncing every logged-in user to /settings on refresh.
    setLoading(true);

    // Set up real-time listener for license updates
    const licenseRef = doc(db, 'users', user.uid, 'license', 'current');

    const unsubscribe = onSnapshot(
      licenseRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const licenseData = docSnapshot.data();
          const isActive = ['active', 'on_trial', 'past_due'].includes(licenseData.status);

          setHasValidLicense(isActive);
          setCustomerPortalUrl(licenseData.customerPortalUrl || null);

          // Update cache (without licenseKey)
          if (isActive) {
            setCachedLicense(user.email!, {
              isValid: isActive,
              expiresAt: licenseData.renewsAt ? new Date(licenseData.renewsAt) : undefined,
            });
          }
        } else {
          // No license document found
          setHasValidLicense(false);
          setCustomerPortalUrl(null);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to license updates:', error);
        // Fall back to cached data or one-time check
        checkUserLicense(user.uid, user.email!)
          .then((response) => {
            setHasValidLicense(response.isValid);
          })
          .catch((err) => {
            console.error('Fallback license check failed:', err);
            setHasValidLicense(false);
          })
          .finally(() => setLoading(false));
      }
    );

    // Cleanup subscription
    return () => unsubscribe();
  }, [user?.uid, user?.email]);

  // Separate effect for subscription ID (zero-knowledge encryption key)
  useEffect(() => {
    if (!user?.uid) {
      setLicenseKey(null);
      setLicenseKeyLoading(false);
      setLicenseKeyError(null);
      return;
    }

    // Try to get from cache first
    const cached = getCachedUserKey(user.uid);
    if (cached) {
      setLicenseKey(cached);
      setLicenseKeyLoading(false);
      setLicenseKeyError(null);
      return;
    }

    // License status not settled yet — keep the key in "loading", don't decide anything
    if (loading) {
      setLicenseKeyLoading(true);
      return;
    }

    // Settled without a valid license: terminal, there is no key to fetch
    if (!hasValidLicense) {
      setLicenseKey(null);
      setLicenseKeyLoading(false);
      setLicenseKeyError(null);
      return;
    }

    // Cache miss - fetch from Cloud Function
    let cancelled = false;
    setLicenseKeyLoading(true);
    setLicenseKeyError(null);
    getUserKey(user.uid)
      .then((key) => {
        if (cancelled) return;
        setLicenseKey(key);
        setLicenseKeyLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching user key:', error);
        if (cancelled) return;
        setLicenseKey(null);
        setLicenseKeyError(error?.message || 'Failed to fetch encryption key');
        setLicenseKeyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.uid, hasValidLicense, loading]);

  // Drop the cached encryption key and fetch a fresh one. Used to self-heal when a
  // stored API key no longer decrypts (e.g. stale cache after a subscription change).
  // Returns the fresh key, or null if it could not be fetched.
  const refreshLicenseKey = useCallback(async (): Promise<string | null> => {
    const uid = userUidRef.current;
    if (!uid) return null;
    clearUserKeyCache();
    setLicenseKeyLoading(true);
    setLicenseKeyError(null);
    try {
      const key = await getUserKey(uid);
      setLicenseKey(key);
      setLicenseKeyLoading(false);
      return key;
    } catch (error: any) {
      console.error('Error refreshing user key:', error);
      setLicenseKey(null);
      setLicenseKeyError(error?.message || 'Failed to fetch encryption key');
      setLicenseKeyLoading(false);
      return null;
    }
  }, []);

  return {
    hasValidLicense,
    loading,
    checkLicense: (userId: string, email: string) => checkUserLicense(userId, email),
    refreshLicense: refreshLicenseFromAPI,
    licenseKey,
    licenseKeyLoading,
    licenseKeyError,
    refreshLicenseKey,
    customerPortalUrl
  };
}; 